<template>
  <div class="dvs-field">
    <label v-if="label" class="dvs-form-label" :for="textInputId">
      {{ label }}
    </label>

    <div
      class="dvs-input-shell dvs-input-shell--with-action"
      :class="{ 'is-error': error, 'has-value': displayText }"
    >
      <input
        :id="textInputId"
        ref="textInputRef"
        class="dvs-input-control"
        type="text"
        inputmode="numeric"
        maxlength="10"
        :placeholder="placeholder"
        :value="displayText"
        @input="handleTextInput"
        @blur="handleBlur"
        @focus="$emit('focus')"
      />

      <input
        ref="nativeDateRef"
        class="dvs-native-picker"
        type="date"
        :value="modelValue"
        @input="handleNativeDateInput"
      />

      <button
        type="button"
        class="dvs-input-action"
        aria-label="Seleccionar fecha"
        @click="openNativePicker"
      >
        <i class="bi bi-calendar3" aria-hidden="true"></i>
      </button>
    </div>

    <p v-if="error && errorMessage" class="dvs-error-message">
      {{ errorMessage }}
    </p>

    <p v-else-if="helper" class="dvs-helper-text">
      {{ helper }}
    </p>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: undefined
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'DD/MM/AAAA'
  },
  helper: {
    type: String,
    default: ''
  },
  error: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus'])

const textInputRef = ref(null)
const nativeDateRef = ref(null)
const displayText = ref(isoToSpanishDate(props.modelValue))

const textInputId = props.id || `dvs-date-${Math.random().toString(36).slice(2)}`

watch(
  () => props.modelValue,
  (value) => {
    displayText.value = isoToSpanishDate(value)
  }
)

function handleTextInput(event) {
  const masked = maskDate(event.target.value)
  displayText.value = masked
  event.target.value = masked

  if (masked.length === 10 && isValidSpanishDate(masked)) {
    emit('update:modelValue', spanishDateToIso(masked))
  }
}

function handleBlur() {
  if (displayText.value && !isValidSpanishDate(displayText.value)) {
    displayText.value = ''
    emit('update:modelValue', '')
  }

  emit('blur')
}

function handleNativeDateInput(event) {
  const isoValue = event.target.value

  emit('update:modelValue', isoValue)
  displayText.value = isoToSpanishDate(isoValue)
}

function openNativePicker() {
  const picker = nativeDateRef.value

  if (!picker) return

  if (typeof picker.showPicker === 'function') {
    picker.showPicker()
    return
  }

  picker.focus()
  picker.click()
}

function maskDate(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8)

  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

function isValidSpanishDate(value) {
  const [day, month, year] = value.split('/').map(Number)

  if (!day || !month || !year) return false
  if (year < 2000 || year > 2100) return false

  const date = new Date(year, month - 1, day)

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

function spanishDateToIso(value) {
  const [day, month, year] = value.split('/')

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

function isoToSpanishDate(value) {
  if (!value) return ''

  const [year, month, day] = value.split('-')

  if (!year || !month || !day) return ''

  return `${day}/${month}/${year}`
}
</script>
